import { PMS_BOARD_LABELS, type PmsBoard, type VegaPlanGap } from '@lemontaigu/pms-core';

// « Vega facture 5 chambres en Pdj, la répartition en compte 9 » — the booking
// form's and the planning pane's warning that Vega's two records of a group
// disagree. Vega keeps the group's charges and its room allocation (the rooms,
// their board and the number of people in each) in two places and never checks
// one against the other (operator, 2026-10-22/23). The PMS reads the charges
// against the allocation night by night (`pms_reservation_vega_plan_gap`).
//
// It is a flag, never a correction: the total stays Vega's, and the charges can be
// the right record — RIVENDELL 022732 is billed as the operator wants, and its
// allocation still says otherwise. Hence « à vérifier », not « à corriger ».
//
// Both screens show the same words, so they share this (as advanceView.ts).

/** null while reading or when there is nothing to say, a message when the read failed. */
export type PlanGapRead = VegaPlanGap | string | null;

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const inBoard = (b: PmsBoard | null) => (b === null ? '' : ` en ${PMS_BOARD_LABELS[b]}`);

function what(g: VegaPlanGap): string {
  switch (g.kind) {
    case 'rooms':
      return `Vega facture ${plural(g.billed, 'chambre', 'chambres')}${inBoard(g.board)}, la répartition en compte ${g.rooms}`;
    case 'persons':
      return (
        `Vega facture ${plural(g.billed, 'personne', 'personnes')}${inBoard(g.board)}, la répartition en compte ${g.persons ?? 0}` +
        // 024714: « DEMI PENSION GROUPE » 26 × 85 € for 26 rooms and 57 people
        (g.billed === g.rooms ? ' — autant que de logements : facturé par chambre ?' : '')
      );
    case 'board':
      return g.allocationBoard === null
        ? `Vega facture ${plural(g.billed, 'chambre', 'chambres')}${inBoard(g.board)}, la répartition n’en met aucune${inBoard(g.board)}`
        : `Vega facture ${plural(g.billed, 'chambre', 'chambres')}${inBoard(g.board)}, la répartition les met${inBoard(g.allocationBoard)}`;
    case 'unbilled':
      return `La répartition met ${plural(g.rooms, 'logement', 'logements')}${inBoard(g.board)}, Vega n’en facture aucun${inBoard(g.board)}`;
    case 'occupancy': {
      const k = plural(g.occupancy ?? 0, 'personne', 'personnes');
      const have =
        g.rooms === 0
          ? `aucun logement de la répartition n’a ${k}`
          : `seul${g.rooms > 1 ? 's' : ''} ${plural(g.rooms, 'logement de la répartition a', 'logements de la répartition ont')} ${k}`;
      return `Vega facture ${plural(g.billed, 'chambre', 'chambres')}${inBoard(g.board)} pour ${k}, ${have}`;
    }
  }
}

/** The one line: what the two records say, and on which night when not on all of them. */
export function planGapText(g: VegaPlanGap): string {
  const line = what(g);
  if (g.gapNights >= g.planNights) return line;
  const others = g.gapNights - 1;
  return `${line} le ${dm(g.firstNight)}${others > 0 ? ` (et ${plural(others, 'autre nuit', 'autres nuits')})` : ''}`;
}

/** What follows the line on screen: what the per-room prices are, and where to look. */
export const planGapNote = (g: VegaPlanGap): string =>
  g.split ? 'à vérifier dans Vega' : 'prix par logement répartis par le PMS, à vérifier dans Vega';

/** Why it matters and what to do, for a tooltip. */
export function planGapTitle(g: VegaPlanGap): string {
  return [
    'Vega tient la facturation du groupe et la répartition des chambres (logements, formule, nombre de personnes) dans deux saisies qu’il ne compare pas.',
    g.split
      ? 'Les prix par logement sont ceux de la facturation Vega.'
      : 'Les prix par logement sont répartis par le PMS ; le total est celui de Vega.',
    'Rien n’est corrigé ici : à vérifier dans Vega.',
    ...(g.detail === null ? [] : [`Relecture Vega : ${g.detail}`]),
  ].join('\n');
}
